import {
	ApolloClient,
	ApolloLink,
	HttpLink,
	InMemoryCache,
	type NormalizedCacheObject,
	type Operation,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { relayStylePagination } from "@apollo/client/utilities";
import merge from "deepmerge";
import { isEqual } from "lodash";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { env } from "~/env.mjs";
import { getApiUrl } from "./server";

import createUploadLink from "apollo-upload-client/createUploadLink.mjs";

export const APOLLO_STATE_PROP_NAME = "__APOLLO_STATE__";

const isServer: boolean = typeof window === "undefined";

let apolloClient: ApolloClient<NormalizedCacheObject>;

function createApolloClient(locale?: string) {
	const uri = `${getApiUrl(locale)}/graphql/api`;

	const batchLink = new HttpLink({
		uri,
		headers: { "User-Agent": "stulpr-frontend" },
	});

	const authLink = setContext(async (_, { headers, token }) => {
		if (!token) {
			token = isServer ? false : localStorage.getItem("auth");
		}

		if (token) {
			return {
				headers: { ...headers, "X-API-KEY": token },
			};
		}

		return { headers };
	});

	const magentoUploadLink = createUploadLink({
		uri,
		headers: { "User-Agent": "stulpr-frontend" },
	});

	const magentoLink = ApolloLink.split(
		(operation: Operation) => operation.getContext().clientName === "magento_upload",
		authLink.concat(magentoUploadLink),
		authLink.concat(batchLink),
	);

	const pimcoreLink = createUploadLink({
		uri: env.NEXT_PUBLIC_PIMCORE_API_URL,
	});

	// const pagination = relayStylePagination();

	return new ApolloClient({
		ssrMode: isServer,
		link: ApolloLink.split(
			(operation) => operation.getContext().clientName === "pim",
			pimcoreLink,
			magentoLink,
		),
		cache: new InMemoryCache({
			possibleTypes: {
				AddressInterface: ["ShippingCartAddress", "BillingCartAddress"],
			},
			typePolicies: {
				OptimizationItem: {
					keyFields: false,
				},
				Query: {
					fields: {
						getDIYProjectListing: relayStylePagination(["filter"]),
					},
				},
				Cart: {
					fields: {
						items: {
							// biome-ignore lint/style/useDefaultParameterLast: existing should be first
							merge(_existing = [], incoming: []) {
								return incoming;
							},
						},
					},
				},
				SelectedPaymentMethod: {
					keyFields: ["code"],
				},
				ShippingCarrier: {
					keyFields: ["carrierCode"],
				},
				SelectedShippingCarrier: {
					keyFields: ["carrierCode"],
				},
			},
		}),
	});
}

export function initializeApollo(
	locale: string,
	initialState: NormalizedCacheObject | null = null,
) {
	const _apolloClient = apolloClient ?? createApolloClient(locale);

	// If your page has Next.js data fetching methods that use Apollo Client, the initial state
	// gets hydrated here
	if (initialState) {
		// Get existing cache, loaded during client side data fetching
		const existingCache = _apolloClient.extract();

		// Merge the existing cache into data passed from getStaticProps/getServerSideProps
		const data = merge(initialState, existingCache, {
			// combine arrays using object equality (like in sets)
			arrayMerge: (destinationArray, sourceArray) => [
				...sourceArray,
				...destinationArray.filter((d) => sourceArray.every((s) => !isEqual(d, s))),
			],
		});

		// Restore the cache with the merged data
		_apolloClient.cache.restore(data);
	}
	// For SSG and SSR always create a new Apollo Client
	if (isServer) return _apolloClient;
	// Create the Apollo Client once in the client
	if (!apolloClient) apolloClient = _apolloClient;

	return _apolloClient;
}

export function addApolloState(
	client: ApolloClient<NormalizedCacheObject>,
	pageProps: { props: Record<string, unknown> },
) {
	if (pageProps?.props) {
		pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
	}

	return pageProps;
}

export function useApollo(pageProps: Record<string, unknown>) {
	const router = useRouter();
	const locale = (router.query.lang as string | undefined) ?? "nl-nl";

	const state = pageProps[APOLLO_STATE_PROP_NAME] as NormalizedCacheObject | null;

	const store = useMemo(() => initializeApollo(locale, state), [locale, state]);

	return store;
}
